import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { api } from "../api";
import { loadGoogleIdentity, googleIdentityId, signInWithGoogleNative } from "../googleAuth";
import { useToast } from "../ToastContext";
import { useLanguage } from "../i18n";

const isNative = Capacitor.isNativePlatform();

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.4 0-13.8 4.1-17.2 10.1z" />
      <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2 14-5.3l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.9 39.6 16.4 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.5 5.5C39.6 37 44 31 44 24c0-1.3-.1-2.7-.4-3.5z" />
    </svg>
  );
}

// Renders nothing until /api/config reports a googleClientId — same
// graceful-degradation pattern as the Razorpay "Pay" button switching on
// razorpayKeyId, so this stays invisible until a real client ID is set.
export function GoogleSignInButton({ onCredential }: { onCredential: (credential: string) => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [clientId, setClientId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    api.get("/api/config").then(({ googleClientId }) => setClientId(googleClientId)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNative || !clientId || !ref.current) return;
    let cancelled = false;
    loadGoogleIdentity()
      .then(() => {
        const id = googleIdentityId();
        if (cancelled || !ref.current || !id) return;
        id.initialize({
          client_id: clientId,
          callback: (resp) => onCredentialRef.current(resp.credential),
        });
        ref.current.innerHTML = "";
        id.renderButton(ref.current, {
          theme: "outline",
          size: "large",
          width: 280,
          text: "continue_with",
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;

  // The native app can't embed Google's own button (Credential Manager has
  // no equivalent) — a normal app button triggers the native account picker
  // instead, styled to match the rest of this screen.
  if (isNative) {
    async function signIn() {
      setBusy(true);
      try {
        const idToken = await signInWithGoogleNative(clientId!);
        if (idToken) onCredentialRef.current(idToken);
        // idToken === null means the user dismissed the account picker — nothing to do.
      } catch (err) {
        // Logged (not just toasted) since a native rejection here can carry
        // the real reason in its message with nothing else surfacing it —
        // e.g. the plugin silently rejects on unsupported options with no
        // Logcat output at all, which cost real debugging time once already.
        console.error("Native Google sign-in failed:", err);
        // The technical reason is appended so a failure on a real phone can
        // be diagnosed from a screenshot: release builds hide the WebView
        // console and the plugin often fails without writing to Logcat.
        const e = err as { message?: string; code?: string } | null;
        const detail = String(e?.message ?? e?.code ?? err ?? "").replace(/\s+/g, " ").slice(0, 140);
        toast(detail ? `${t("auth.googleSignInFailedToast")} [${detail}]` : t("auth.googleSignInFailedToast"));
      } finally {
        setBusy(false);
      }
    }
    return (
      <button
        type="button"
        className="btn-outline"
        disabled={busy}
        onClick={signIn}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, margin: "6px 0" }}
      >
        <GoogleGlyph />
        {t("auth.continueWithGoogle")}
      </button>
    );
  }

  return <div ref={ref} style={{ display: "flex", justifyContent: "center", margin: "6px 0" }} />;
}
